"use client";

import { useSmartLocale, useSmartTranslations } from "../hooks/useSmartTranslations";
import { cn } from "../utils/cn";
import { formatDateShort, parseDateString } from "../utils/date";
import { useShadCNAnimations } from "../utils/animations";
import { Calendar, ChevronLeft, ChevronRight, Sparkles, X as XIcon } from "lucide-react";
import * as React from "react";
import { useId } from "react";
import { Popover } from "./Popover";

/** Public props for the `DatePicker` component. */
export interface DatePickerProps {
  id?: string;
  value?: Date;
  onChange: (date: Date | undefined) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  size?: "sm" | "md" | "lg";
  label?: string;
  /** Custom class for label */
  labelClassName?: string;
  required?: boolean;
  todayLabel?: string;
  clearLabel?: string;
  weekdayLabels?: string[];
  /** Disable selecting past dates (before today) */
  disablePastDates?: boolean;
  /** Minimum selectable date (inclusive). Compared by day in local timezone. */
  minDate?: Date;
  /** Maximum selectable date (inclusive). Compared by day in local timezone. */
  maxDate?: Date;
}

export const DatePicker: React.FC<DatePickerProps> = ({
  id,
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
  size = "md",
  label,
  labelClassName,
  required,
  todayLabel,
  clearLabel,
  weekdayLabels,
  disablePastDates = false,
  minDate,
  maxDate,
}) => {
  const t = useSmartTranslations("DatePicker");
  const tv = useSmartTranslations("ValidationInput");
  const locale = useSmartLocale();
  const [isOpen, setIsOpen] = React.useState(false);
  const [viewDate, setViewDate] = React.useState(value || new Date());
  const [viewMode, setViewMode] = React.useState<"calendar" | "month" | "year">("calendar");
  const [localRequiredError, setLocalRequiredError] = React.useState<string | undefined>();
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const wheelContainerRef = React.useRef<HTMLDivElement>(null);
  const wheelDeltaRef = React.useRef(0);

  // Focus and typing state
  const [isFocused, setIsFocused] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");

  React.useEffect(() => {
    if (value) {
      const parsed = parseDateString(inputValue, locale);
      const isSame = parsed && parsed.getTime() === value.getTime();
      if (!isSame) {
        setInputValue(formatDateShort(value, locale));
      }
    } else if (!isFocused) {
      setInputValue("");
    }
  }, [value, locale, isFocused]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInputValue(text);
    const parsedDate = parseDateString(text, locale);
    if (parsedDate) {
      if (!isDateDisabled(parsedDate)) {
        onChange(parsedDate);
        setViewDate(parsedDate);
        setLocalRequiredError(undefined);
      }
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    if (!inputValue.trim()) {
      if (required) {
        setLocalRequiredError(tv("required"));
      } else {
        onChange(undefined);
        setLocalRequiredError(undefined);
      }
    } else {
      const parsedDate = parseDateString(inputValue, locale);
      if (parsedDate && !isDateDisabled(parsedDate)) {
        onChange(parsedDate);
        setInputValue(formatDateShort(parsedDate, locale));
        setLocalRequiredError(undefined);
      } else {
        if (value) {
          setInputValue(formatDateShort(value, locale));
          setLocalRequiredError(undefined);
        } else {
          setInputValue("");
          if (required) {
            setLocalRequiredError(tv("required"));
          }
        }
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const parsedDate = parseDateString(inputValue, locale);
      if (parsedDate && !isDateDisabled(parsedDate)) {
        onChange(parsedDate);
        setInputValue(formatDateShort(parsedDate, locale));
        setViewDate(parsedDate);
        setIsOpen(false);
        setLocalRequiredError(undefined);
      } else if (!inputValue.trim() && !required) {
        onChange(undefined);
        setIsOpen(false);
        setLocalRequiredError(undefined);
      } else {
        setIsOpen(false);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  const normalizeToLocalDay = React.useCallback((date: Date | undefined): Date | null => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  }, []);

  const minDay = React.useMemo(() => normalizeToLocalDay(minDate), [minDate, normalizeToLocalDay]);
  const maxDay = React.useMemo(() => normalizeToLocalDay(maxDate), [maxDate, normalizeToLocalDay]);

  const isDateDisabled = React.useCallback(
    (date: Date) => {
      const day = normalizeToLocalDay(date);
      if (!day) return false;

      if (disablePastDates) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (day < today) return true;
      }

      if (minDay && day < minDay) return true;
      if (maxDay && day > maxDay) return true;
      return false;
    },
    [disablePastDates, maxDay, minDay, normalizeToLocalDay],
  );

  // Inject ShadCN animations
  useShadCNAnimations();

  // Size styles for consistent sizing across all elements
  const sizeStyles = {
    sm: {
      trigger: "px-2.5 py-1.5 text-sm h-8 md:h-7 md:text-xs md:py-1",
      dayCell: "w-7 h-7 text-xs",
      monthCell: "w-6 h-6 text-xs",
      navButton: "p-1.5",
      navIcon: "h-3 w-3",
      calendarIcon: "h-3.5 w-3.5",
      clearIcon: "w-2.5 h-2.5",
      actionButton: "px-2 py-1 text-[10px] gap-1",
      actionIcon: "w-2.5 h-2.5",
      contentWidth: 240,
      contentPadding: "p-4",
      label: "text-xs",
      weekdayLabel: "text-[9px] py-1",
      headerMargin: "mb-2",
      footerMargin: "mt-3 pt-2 gap-1.5",
    },
    md: {
      trigger: "px-3 py-2.5 text-sm h-10",
      dayCell: "w-8 h-8 text-sm",
      monthCell: "w-8 h-8 text-sm",
      navButton: "p-2",
      navIcon: "h-4 w-4",
      calendarIcon: "h-4 w-4",
      clearIcon: "w-3.5 h-3.5",
      actionButton: "px-3 py-2 text-xs gap-2",
      actionIcon: "w-3.5 h-3.5",
      contentWidth: 280,
      contentPadding: "p-5",
      label: "text-sm",
      weekdayLabel: "text-[10px] py-1.5",
      headerMargin: "mb-4",
      footerMargin: "mt-4 pt-3 gap-2",
    },
    lg: {
      trigger: "px-4 py-3 text-base h-12",
      dayCell: "w-10 h-10 text-base",
      monthCell: "w-10 h-10 text-base",
      navButton: "p-2.5",
      navIcon: "h-5 w-5",
      calendarIcon: "h-5 w-5",
      clearIcon: "w-4 h-4",
      actionButton: "px-4 py-2.5 text-sm gap-2",
      actionIcon: "w-4 h-4",
      contentWidth: 340,
      contentPadding: "p-6",
      label: "text-base",
      weekdayLabel: "text-xs py-2",
      headerMargin: "mb-5",
      footerMargin: "mt-5 pt-4 gap-2.5",
    },
  } as const;

  // Keep calendar month synced with current value or today
  React.useEffect(() => {
    if (value) {
      setViewDate(value);
    } else {
      setViewDate(new Date());
    }
  }, [value]);

  React.useEffect(() => {
    if (disabled || !required || value) {
      setLocalRequiredError(undefined);
    }
  }, [disabled, required, value]);

  // Reset view mode when popover closes
  React.useEffect(() => {
    if (!isOpen) {
      setViewMode("calendar");
    }
  }, [isOpen]);

  const handleDateSelect = (date: Date) => {
    // Create date in local timezone, not UTC
    // Preserve time from existing value if it's not midnight; otherwise use current time
    let selectedDate: Date;
    if (value && (value.getHours() !== 0 || value.getMinutes() !== 0 || value.getSeconds() !== 0)) {
      // Preserve existing time
      selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), value.getHours(), value.getMinutes(), value.getSeconds());
    } else {
      // Use current time
      const now = new Date();
      selectedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate(), now.getHours(), now.getMinutes(), now.getSeconds());
    }
    onChange(selectedDate);
    setLocalRequiredError(undefined);
    setIsOpen(false);
  };

  const formatDateDisplay = (date: Date): string => {
    // Use locale for date formatting
    return date.toLocaleDateString(locale === "vi" ? "vi-VN" : "en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  const getDaysInMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date): number => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const navigateMonth = React.useCallback((direction: "prev" | "next") => {
    setViewDate((prev) => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + (direction === "next" ? 1 : -1));
      return newDate;
    });
  }, []);

  const isElementVerticallyScrollable = (el: Element) => {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    if (overflowY !== "auto" && overflowY !== "scroll") return false;
    const node = el as HTMLElement;
    return node.scrollHeight > node.clientHeight + 1;
  };

  // Mouse wheel UX similar to Combobox: keep the page from scrolling while hovering the popover.
  // If the popover itself is scrollable (small viewports), allow normal scrolling; otherwise use the wheel to navigate months.
  React.useEffect(() => {
    if (!isOpen) return;
    const container = wheelContainerRef.current;
    if (!container) return;

    const onWheel = (event: WheelEvent) => {
      if (!container.contains(event.target as Node)) return;
      if (event.ctrlKey) return;

      // If the wheel originates from a scrollable element inside the popover, don't hijack it.
      let node: Element | null = event.target as Element | null;
      while (node && node !== container) {
        if (isElementVerticallyScrollable(node)) return;
        node = node.parentElement;
      }
      if (isElementVerticallyScrollable(container)) return;

      event.preventDefault();
      event.stopPropagation();

      wheelDeltaRef.current += event.deltaY;
      const threshold = 70;
      if (Math.abs(wheelDeltaRef.current) < threshold) return;

      const steps = Math.trunc(wheelDeltaRef.current / threshold);
      wheelDeltaRef.current -= steps * threshold;

      const direction: "prev" | "next" = steps > 0 ? "next" : "prev";
      for (let i = 0; i < Math.abs(steps); i++) navigateMonth(direction);
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [isOpen, navigateMonth]);

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(viewDate);
    const firstDayOfMonth = getFirstDayOfMonth(viewDate);
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-${i}`} className={sizeStyles[size].dayCell.split(" ").slice(0, 2).join(" ")} />);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
      const isSelected =
        value && date.getDate() === value.getDate() && date.getMonth() === value.getMonth() && date.getFullYear() === value.getFullYear();
      const isToday = date.toDateString() === new Date().toDateString();

      const isDisabled = isDateDisabled(date);

      // Calculate which row this day is in (0-based)
      const totalDaysFromStart = firstDayOfMonth + day - 1;
      const rowIndex = Math.floor(totalDaysFromStart / 7);

      days.push(
        <button
          key={day}
          onClick={() => !isDisabled && handleDateSelect(date)}
          disabled={isDisabled}
          style={{
            animationDelay: isOpen ? `${rowIndex * 40}ms` : "0ms",
          }}
          className={cn(
            sizeStyles[size].dayCell,
            "datepicker-day rounded-lg focus:outline-none relative cursor-pointer",
            "transition-all duration-200 font-medium",
            isDisabled && "opacity-30 cursor-not-allowed text-muted-foreground",
            isSelected
              ? "bg-linear-to-br from-primary to-primary/80 text-primary-foreground font-bold shadow-lg shadow-primary/30 scale-110 z-10 hover:from-primary hover:to-primary/70"
              : !isDisabled && "hover:bg-accent/80 hover:text-accent-foreground hover:scale-105 focus:bg-accent focus:text-accent-foreground",
            isToday && !isSelected && "bg-primary/15 text-primary font-bold ring-2 ring-primary/30",
          )}
        >
          {day}
          {isToday && !isSelected && <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-primary" />}
        </button>,
      );
    }

    return days;
  };

  // Render month selector grid
  const renderMonthSelector = () => {
    const months =
      locale === "vi"
        ? ["Th1", "Th2", "Th3", "Th4", "Th5", "Th6", "Th7", "Th8", "Th9", "Th10", "Th11", "Th12"]
        : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    return (
      <div className="grid grid-cols-3 gap-2 p-2">
        {months.map((month, idx) => {
          const isSelected = viewDate.getMonth() === idx;
          return (
            <button
              key={month}
              type="button"
              onClick={() => {
                setViewDate(new Date(viewDate.getFullYear(), idx, 1));
                setViewMode("calendar");
              }}
              className={cn(
                "py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200",
                isSelected ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent/80 text-foreground hover:scale-105",
              )}
            >
              {month}
            </button>
          );
        })}
      </div>
    );
  };

  // Render year selector grid
  const renderYearSelector = () => {
    const currentYear = viewDate.getFullYear();
    const startYear = Math.floor(currentYear / 12) * 12;
    const years = Array.from({ length: 12 }, (_, i) => startYear + i);

    return (
      <div className="grid grid-cols-3 gap-2 p-2">
        {years.map((year) => {
          const isSelected = viewDate.getFullYear() === year;
          return (
            <button
              key={year}
              type="button"
              onClick={() => {
                setViewDate(new Date(year, viewDate.getMonth(), 1));
                setViewMode("month");
              }}
              className={cn(
                "py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200",
                isSelected ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent/80 text-foreground hover:scale-105",
              )}
            >
              {year}
            </button>
          );
        })}
      </div>
    );
  };

  // Navigate year range for year selector
  const navigateYearRange = (direction: "prev" | "next") => {
    const currentYear = viewDate.getFullYear();
    const offset = direction === "next" ? 12 : -12;
    setViewDate(new Date(currentYear + offset, viewDate.getMonth(), 1));
  };

  const datePickerContent = (
    <div ref={wheelContainerRef} data-datepicker className="w-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-1">
        <button
          type="button"
          onClick={() => (viewMode === "year" ? navigateYearRange("prev") : navigateMonth("prev"))}
          className={cn(
            "p-2 rounded-xl transition-all duration-200",
            "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground",
            "hover:scale-110 active:scale-95 hover:shadow-md",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
          )}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setViewMode(viewMode === "month" ? "calendar" : "month")}
            className={cn(
              "text-sm font-bold transition-all duration-200 px-2 py-1 rounded-lg",
              viewMode === "month" ? "bg-primary/15 text-primary" : "text-foreground hover:bg-accent/50",
            )}
          >
            {viewDate.toLocaleDateString(locale === "vi" ? "vi-VN" : "en-US", { month: "long" })}
          </button>
          <button
            type="button"
            onClick={() => setViewMode(viewMode === "year" ? "calendar" : "year")}
            className={cn(
              "text-sm font-bold transition-all duration-200 px-2 py-1 rounded-lg",
              viewMode === "year" ? "bg-primary/15 text-primary" : "text-foreground hover:bg-accent/50",
            )}
          >
            {viewDate.getFullYear()}
          </button>
        </div>
        <button
          type="button"
          onClick={() => (viewMode === "year" ? navigateYearRange("next") : navigateMonth("next"))}
          className={cn(
            "p-2 rounded-xl transition-all duration-200",
            "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground",
            "hover:scale-110 active:scale-95 hover:shadow-md",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
          )}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Content based on view mode */}
      {viewMode === "calendar" && (
        <>
          {/* Weekday headers */}
          <div className={cn("grid grid-cols-7 gap-1 mb-2 px-0.5")}>
            {(weekdayLabels || (locale === "vi" ? ["CN", "T2", "T3", "T4", "T5", "T6", "T7"] : ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"])).map(
              (day, idx) => (
                <div
                  key={day}
                  className={cn(
                    "text-center font-bold uppercase tracking-wide",
                    sizeStyles[size].weekdayLabel,
                    idx === 0 || idx === 6 ? "text-primary/70" : "text-muted-foreground/60",
                  )}
                >
                  {day}
                </div>
              ),
            )}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1 p-1 rounded-xl bg-muted/20">{renderCalendar()}</div>
        </>
      )}

      {viewMode === "month" && renderMonthSelector()}
      {viewMode === "year" && renderYearSelector()}

      {/* Footer actions */}
      <div className={cn("flex items-center border-t border-border/50", sizeStyles[size].footerMargin)}>
        <button
          type="button"
          onClick={() => {
            const today = new Date();
            if (isDateDisabled(today)) return;
            handleDateSelect(today);
          }}
          disabled={isDateDisabled(new Date())}
          className={cn(
            "flex-1 font-semibold rounded-xl",
            "bg-linear-to-r from-primary/10 to-primary/5 border border-primary/30",
            "text-primary hover:from-primary/20 hover:to-primary/10 hover:border-primary/50",
            "transition-all duration-300 flex items-center justify-center",
            "hover:scale-[1.02] active:scale-[0.98] hover:shadow-md hover:shadow-primary/10",
            sizeStyles[size].actionButton,
            isDateDisabled(new Date()) && "opacity-50 cursor-not-allowed hover:scale-100 active:scale-100",
          )}
        >
          <Sparkles className={sizeStyles[size].actionIcon} />
          {todayLabel || t("today")}
        </button>
        <button
          type="button"
          onClick={() => {
            onChange(undefined);
            setIsOpen(false);
            setViewDate(new Date());
          }}
          className={cn(
            "flex-1 font-semibold rounded-xl",
            "bg-linear-to-r from-destructive/10 to-destructive/5 border border-destructive/30",
            "text-destructive hover:from-destructive/20 hover:to-destructive/10 hover:border-destructive/50",
            "transition-all duration-300 flex items-center justify-center",
            "hover:scale-[1.02] active:scale-[0.98] hover:shadow-md hover:shadow-destructive/10",
            sizeStyles[size].actionButton,
          )}
        >
          <XIcon className={sizeStyles[size].actionIcon} />
          {clearLabel || t("clear")}
        </button>
      </div>
    </div>
  );

  const autoId = useId();
  const resolvedId = id ? String(id) : `datepicker-${autoId}`;
  const labelId = label ? `${resolvedId}-label` : undefined;
  const labelSize = sizeStyles[size].label;

  // Radius consistent with Input: sm => rounded-md, md/lg => rounded-lg
  const radiusClass = size === "sm" ? "rounded-md" : "rounded-lg";
  const verticalGap = size === "sm" ? "space-y-1.5" : size === "lg" ? "space-y-2.5" : "space-y-2";
  const effectiveError = localRequiredError;

  return (
    <div className={cn("w-full group", verticalGap)}>
      {label && (
        <div className="flex items-center justify-between mb-2">
          <label
            id={labelId}
            htmlFor={resolvedId}
            onClick={() => inputRef.current?.focus()}
            className={cn(
              labelSize,
              "font-semibold transition-colors duration-300 cursor-pointer",
              disabled ? "text-muted-foreground" : "text-foreground group-focus-within:text-primary hover:text-primary",
              effectiveError && "text-destructive",
              labelClassName,
            )}
          >
            {label}
            {required && <span className="text-destructive ml-1 animate-pulse">*</span>}
          </label>
        </div>
      )}
      <input
        tabIndex={-1}
        aria-hidden="true"
        value={value ? "selected" : ""}
        onChange={() => {}}
        required={required}
        disabled={disabled}
        onInvalid={(e) => {
          e.preventDefault();
          setLocalRequiredError(tv("required"));
        }}
        className="pointer-events-none absolute h-0 w-0 opacity-0"
      />
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-start"
        disabled={disabled}
        contentWidth={sizeStyles[size].contentWidth}
        contentClassName={cn(
          "p-0",
          "backdrop-blur-xl bg-popover/95 border-border/40 shadow-2xl",
          "rounded-2xl md:rounded-3xl",
          // Keep usable on small viewports (wheel scroll should stay within the popover if it overflows)
          "max-w-[calc(100vw-1rem)] max-h-[calc(100vh-6rem)] overflow-auto overscroll-contain",
          sizeStyles[size].contentPadding,
          "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-300",
        )}
        trigger={
          <div
            ref={triggerRef}
            id={resolvedId}
            role="button"
            aria-label={value ? formatDateDisplay(value) : (placeholder || t("placeholder"))}
            aria-labelledby={labelId}
            aria-required={required}
            aria-invalid={!!effectiveError}
            className={cn(
              "group flex w-full items-center justify-between border bg-background/80 backdrop-blur-sm",
              "rounded-full",
              sizeStyles[size].trigger,
              disabled
                ? "border-border/40 opacity-50 cursor-not-allowed"
                : [
                    "border-border/60 hover:border-primary/40",
                    "focus-within:ring-2 focus-within:ring-primary/50 focus-within:ring-offset-2 focus-within:ring-offset-background focus-within:border-transparent focus-within:hover:border-transparent",
                    "hover:bg-accent/10 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5",
                  ],
              "transition-all duration-300 ease-out",
              isOpen && !isFocused && "ring-2 ring-primary/30 border-primary/50 shadow-lg shadow-primary/10",
              effectiveError && "border-destructive/60 focus-within:ring-destructive/50 bg-destructive/5",
              className,
            )}
          >
            <div className="flex items-center gap-2.5 flex-1 min-w-0">
              <div
                className={cn(
                  "flex items-center justify-center transition-colors duration-300",
                  isOpen ? "text-primary" : "text-muted-foreground group-hover:text-primary",
                )}
              >
                <Calendar className={cn(sizeStyles[size].calendarIcon, "transition-transform duration-300", isOpen && "scale-110")} />
              </div>
              <input
                ref={inputRef}
                type="text"
                aria-label={value ? formatDateDisplay(value) : (placeholder || t("placeholder"))}
                disabled={disabled}
                placeholder={placeholder || t("placeholder")}
                value={isFocused ? inputValue : (value ? formatDateDisplay(value) : "")}
                onChange={handleInputChange}
                onFocus={() => {
                  setIsFocused(true);
                  if (!disabled) setIsOpen(true);
                }}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onClick={(e) => {
                  if (isOpen) {
                    e.stopPropagation();
                  }
                }}
                className="w-full bg-transparent border-none outline-none focus:outline-none p-0 text-foreground font-medium placeholder-muted-foreground/60 min-w-0"
              />
            </div>
            <div className="flex items-center gap-1 shrink-0">
              {value && (
                <span
                  role="button"
                  aria-label={clearLabel || t("clear")}
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onChange(undefined);
                    setViewDate(new Date());
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      onChange(undefined);
                      setLocalRequiredError(undefined);
                      setViewDate(new Date());
                    }
                  }}
                  className={cn(
                    "inline-flex items-center justify-center rounded-lg p-1",
                    "text-muted-foreground hover:text-destructive hover:bg-destructive/10",
                    "transition-all duration-200 cursor-pointer hover:scale-110",
                  )}
                >
                  <XIcon className="h-3.5 w-3.5" />
                </span>
              )}
              <span
                className={cn("transition-all duration-300 text-muted-foreground group-hover:text-foreground", isOpen && "rotate-180 text-primary")}
              >
                <svg className={sizeStyles[size].calendarIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                </svg>
              </span>
            </div>
          </div>
        }
      >
        {datePickerContent}
      </Popover>
      {effectiveError && <div className="text-xs text-destructive">{effectiveError}</div>}
    </div>
  );
};

/** Public props for the `DateRangePicker` component. */
export interface DateRangePickerProps {
  id?: string;
  startDate?: Date;
  endDate?: Date | null;
  onChange: (start: Date | undefined, end: Date | null | undefined) => void;
  placeholder?: string;
  className?: string;
  label?: string;
  labelClassName?: string;
  required?: boolean;
  /** Disable selecting past dates (before today) */
  disablePastDates?: boolean;
  /** Minimum selectable date (inclusive). Compared by day in local timezone. */
  minDate?: Date;
  /** Maximum selectable date (inclusive). Compared by day in local timezone. */
  maxDate?: Date;
  /** Size variant */
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
}

export const DateRangePicker: React.FC<DateRangePickerProps> = ({
  id,
  startDate,
  endDate,
  onChange,
  placeholder = "Select date range...",
  className,
  label,
  labelClassName,
  required = false,
  disablePastDates = false,
  minDate,
  maxDate,
  size = "md",
  disabled = false,
}) => {
  const locale = useSmartLocale();
  const t = useSmartTranslations("DatePicker");
  const tv = useSmartTranslations("ValidationInput");
  const [isOpen, setIsOpen] = React.useState(false);
  const [viewMode, setViewMode] = React.useState<"calendar" | "month" | "year">("calendar");
  const [localRequiredError, setLocalRequiredError] = React.useState<string | undefined>();
  const wheelContainerRef = React.useRef<HTMLDivElement>(null);
  const wheelDeltaRef = React.useRef(0);

  const triggerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);

  // Focus and typing state
  const [isFocused, setIsFocused] = React.useState(false);
  const [inputValue, setInputValue] = React.useState<string>("");

  const todayDate = React.useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, []);

  const getRangeString = React.useCallback((s: Date | null | undefined, e: Date | null | undefined) => {
    if (!s) return "";
    const startStr = formatDateShort(s, locale);
    if (!e) return `${startStr} - `;
    return `${startStr} - ${formatDateShort(e, locale)}`;
  }, [locale]);

  React.useEffect(() => {
    if (startDate) {
      const parts = inputValue.split(/\s*-\s*/);
      const inputStart = parts[0] ? parseDateString(parts[0], locale) : null;
      const inputEnd = parts[1] ? parseDateString(parts[1], locale) : null;
      const startSame = inputStart && startDate && inputStart.getTime() === startDate.getTime();
      const endSame = (!endDate && !inputEnd) || (endDate && inputEnd && endDate.getTime() === inputEnd.getTime());
      
      if (!(startSame && endSame)) {
        setInputValue(getRangeString(startDate, endDate));
      }
    } else if (!isFocused) {
      setInputValue("");
    }
  }, [startDate, endDate, locale, isFocused, getRangeString]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setInputValue(text);
    
    const parts = text.split(/\s*-\s*/);
    if (parts.length === 2) {
      const s = parseDateString(parts[0], locale);
      const eVal = parseDateString(parts[1], locale);
      if (s) {
        const isStartDisabled = (disablePastDates && s < todayDate) || (!!minDay && s < minDay) || (!!maxDay && s > maxDay);
        if (!isStartDisabled) {
          setTempStart(s);
          if (eVal) {
            const isEndDisabled = (disablePastDates && eVal < todayDate) || (!!minDay && eVal < minDay) || (!!maxDay && eVal > maxDay);
            if (!isEndDisabled && s <= eVal) {
              setTempEnd(eVal);
              onChange(s, eVal);
              setViewDate(s);
              setLocalRequiredError(undefined);
            } else {
              setTempEnd(null);
              onChange(s, null);
            }
          } else {
            setTempEnd(null);
            onChange(s, null);
          }
        }
      }
    } else if (parts.length === 1 && parts[0].trim()) {
      const s = parseDateString(parts[0], locale);
      if (s) {
        const isStartDisabled = (disablePastDates && s < todayDate) || (!!minDay && s < minDay) || (!!maxDay && s > maxDay);
        if (!isStartDisabled) {
          setTempStart(s);
          setTempEnd(null);
          onChange(s, null);
        }
      }
    }
  };

  const handleInputBlur = () => {
    setIsFocused(false);
    if (!inputValue.trim()) {
      if (required) {
        setLocalRequiredError(tv("required"));
      } else {
        setTempStart(null);
        setTempEnd(null);
        onChange(undefined, undefined);
        setLocalRequiredError(undefined);
      }
    } else {
      const parts = inputValue.split(/\s*-\s*/);
      const s = parts[0] ? parseDateString(parts[0], locale) : null;
      const eVal = parts[1] ? parseDateString(parts[1], locale) : null;
      
      if (s) {
        const isStartDisabled = (disablePastDates && s < todayDate) || (!!minDay && s < minDay) || (!!maxDay && s > maxDay);
        if (!isStartDisabled) {
          if (eVal) {
            const isEndDisabled = (disablePastDates && eVal < todayDate) || (!!minDay && eVal < minDay) || (!!maxDay && eVal > maxDay);
            if (!isEndDisabled && s <= eVal) {
              setTempStart(s);
              setTempEnd(eVal);
              onChange(s, eVal);
              setInputValue(getRangeString(s, eVal));
              setLocalRequiredError(undefined);
              return;
            }
          } else {
            setTempStart(s);
            setTempEnd(null);
            onChange(s, null);
            setInputValue(getRangeString(s, null));
            setLocalRequiredError(undefined);
            return;
          }
        }
      }
      
      if (startDate) {
        setTempStart(normalizeToLocal(startDate));
        setTempEnd(normalizeToLocal(endDate));
        setInputValue(getRangeString(startDate, endDate));
        setLocalRequiredError(undefined);
      } else {
        setTempStart(null);
        setTempEnd(null);
        setInputValue("");
        if (required) {
          setLocalRequiredError(tv("required"));
        }
      }
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const parts = inputValue.split(/\s*-\s*/);
      const s = parts[0] ? parseDateString(parts[0], locale) : null;
      const eVal = parts[1] ? parseDateString(parts[1], locale) : null;
      
      if (s) {
        const isStartDisabled = (disablePastDates && s < todayDate) || (!!minDay && s < minDay) || (!!maxDay && s > maxDay);
        if (!isStartDisabled) {
          if (eVal) {
            const isEndDisabled = (disablePastDates && eVal < todayDate) || (!!minDay && eVal < minDay) || (!!maxDay && eVal > maxDay);
            if (!isEndDisabled && s <= eVal) {
              setTempStart(s);
              setTempEnd(eVal);
              onChange(s, eVal);
              setInputValue(getRangeString(s, eVal));
              setViewDate(s);
              setIsOpen(false);
              setLocalRequiredError(undefined);
              return;
            }
          } else {
            setTempStart(s);
            setTempEnd(null);
            onChange(s, null);
            setInputValue(getRangeString(s, null));
            setViewDate(s);
            setIsOpen(false);
            setLocalRequiredError(undefined);
            return;
          }
        }
      }
      setIsOpen(false);
    } else if (e.key === "Escape") {
      setIsOpen(false);
    }
  };

  // Size styles for consistent sizing across all elements
  const sizeStyles = {
    sm: {
      trigger: "px-2.5 py-1.5 text-sm h-8 md:h-7 md:text-xs md:py-1",
      dayCell: "w-7 h-7 text-xs",
      monthCell: "w-6 h-6 text-xs",
      navButton: "p-1.5",
      navIcon: "h-3 w-3",
      calendarIcon: "h-3.5 w-3.5",
      clearIcon: "w-2.5 h-2.5",
      actionButton: "px-2 py-1 text-[10px] gap-1",
      actionIcon: "w-2.5 h-2.5",
      contentWidth: 240,
      contentPadding: "p-4",
      label: "text-xs",
      weekdayLabel: "text-[9px] py-1",
      headerMargin: "mb-2",
      footerMargin: "mt-3 pt-2 gap-1.5",
    },
    md: {
      trigger: "px-3 py-2.5 text-sm h-10",
      dayCell: "w-8 h-8 text-sm",
      monthCell: "w-8 h-8 text-sm",
      navButton: "p-2",
      navIcon: "h-4 w-4",
      calendarIcon: "h-4 w-4",
      clearIcon: "w-3.5 h-3.5",
      actionButton: "px-3 py-2 text-xs gap-2",
      actionIcon: "w-3.5 h-3.5",
      contentWidth: 280,
      contentPadding: "p-5",
      label: "text-sm",
      weekdayLabel: "text-[10px] py-1.5",
      headerMargin: "mb-4",
      footerMargin: "mt-4 pt-3 gap-2",
    },
    lg: {
      trigger: "px-4 py-3 text-base h-12",
      dayCell: "w-10 h-10 text-base",
      monthCell: "w-10 h-10 text-base",
      navButton: "p-2.5",
      navIcon: "h-5 w-5",
      calendarIcon: "h-5 w-5",
      clearIcon: "w-4 h-4",
      actionButton: "px-4 py-2.5 text-sm gap-2",
      actionIcon: "w-4 h-4",
      contentWidth: 340,
      contentPadding: "p-6",
      label: "text-base",
      weekdayLabel: "text-xs py-2",
      headerMargin: "mb-5",
      footerMargin: "mt-5 pt-4 gap-2.5",
    },
  } as const;

  // Helper to normalize date to local timezone (avoid UTC offset issues)
  const normalizeToLocal = (date: Date | undefined | null): Date | null => {
    if (!date) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
  };

  const minDay = React.useMemo(() => normalizeToLocal(minDate), [minDate]);
  const maxDay = React.useMemo(() => normalizeToLocal(maxDate), [maxDate]);

  // Use passed-in props as the source of truth, but manage a temporary state for selection.
  const [viewDate, setViewDate] = React.useState<Date>(startDate || new Date());
  const [tempStart, setTempStart] = React.useState<Date | null>(normalizeToLocal(startDate));
  const [tempEnd, setTempEnd] = React.useState<Date | null>(normalizeToLocal(endDate));
  const [hoveredDate, setHoveredDate] = React.useState<Date | null>(null);

  // Sync temp state with props (normalize to avoid timezone issues)
  React.useEffect(() => {
    setTempStart(normalizeToLocal(startDate));
  }, [startDate]);

  React.useEffect(() => {
    setTempEnd(normalizeToLocal(endDate));
  }, [endDate]);

  React.useEffect(() => {
    if (!isOpen) {
      setViewMode("calendar");
    }
  }, [isOpen]);

  React.useEffect(() => {
    if (!required || (startDate && endDate)) {
      setLocalRequiredError(undefined);
    }
  }, [endDate, required, startDate]);

  const isSameDay = (a: Date | null, b: Date | null) => {
    if (!a || !b) return false;
    return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  };
  const inRange = (d: Date, s: Date, e: Date) => d > s && d < e;
  const getDaysInMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1).getDay();

  const navigateMonth = React.useCallback((direction: "prev" | "next") => {
    setViewDate((prev) => new Date(prev.getFullYear(), prev.getMonth() + (direction === "next" ? 1 : -1), 1));
  }, []);

  const navigateYearRange = React.useCallback((direction: "prev" | "next") => {
    setViewDate((prev) => new Date(prev.getFullYear() + (direction === "next" ? 12 : -12), prev.getMonth(), 1));
  }, []);

  const isElementVerticallyScrollable = (el: Element) => {
    const style = window.getComputedStyle(el);
    const overflowY = style.overflowY;
    if (overflowY !== "auto" && overflowY !== "scroll") return false;
    const node = el as HTMLElement;
    return node.scrollHeight > node.clientHeight + 1;
  };

  React.useEffect(() => {
    if (!isOpen) return;
    const container = wheelContainerRef.current;
    if (!container) return;

    const onWheel = (event: WheelEvent) => {
      if (!container.contains(event.target as Node)) return;
      if (event.ctrlKey) return;

      let node: Element | null = event.target as Element | null;
      while (node && node !== container) {
        if (isElementVerticallyScrollable(node)) return;
        node = node.parentElement;
      }
      if (isElementVerticallyScrollable(container)) return;

      event.preventDefault();
      event.stopPropagation();

      wheelDeltaRef.current += event.deltaY;
      const threshold = 70;
      if (Math.abs(wheelDeltaRef.current) < threshold) return;

      const steps = Math.trunc(wheelDeltaRef.current / threshold);
      wheelDeltaRef.current -= steps * threshold;

      const direction: "prev" | "next" = steps > 0 ? "next" : "prev";
      for (let i = 0; i < Math.abs(steps); i++) navigateMonth(direction);
    };

    container.addEventListener("wheel", onWheel, { passive: false });
    return () => container.removeEventListener("wheel", onWheel);
  }, [isOpen, navigateMonth]);

  const handleSelect = (date: Date) => {
    // Create date object with local timezone to avoid UTC offset issues
    const localDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    if (!tempStart || (tempStart && tempEnd)) {
      setTempStart(localDate);
      setTempEnd(null);
      setHoveredDate(null);
      onChange(localDate, null);
    } else if (tempStart && !tempEnd) {
      if (localDate < tempStart) {
        setTempStart(localDate);
        onChange(localDate, null);
      } else {
        setTempEnd(localDate);
        setLocalRequiredError(undefined);
        onChange(tempStart, localDate);
        setIsOpen(false);
      }
    }
  };

  const handleSelectToday = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const localToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const isPastDate = disablePastDates && localToday < today;
    const isOutOfRange = (!!minDay && localToday < minDay) || (!!maxDay && localToday > maxDay);
    if (isPastDate || isOutOfRange) return;

    setTempStart(localToday);
    setTempEnd(localToday);
    setHoveredDate(null);
    setLocalRequiredError(undefined);
    onChange(localToday, localToday);
    setIsOpen(false);
  };

  const handleClear = () => {
    setTempStart(null);
    setTempEnd(null);
    setHoveredDate(null);
    onChange(undefined, undefined);
    setIsOpen(false);
  };

  const renderGrid = () => {
    const nodes: React.ReactNode[] = [];
    const daysInMonth = getDaysInMonth(viewDate);
    const firstDay = getFirstDayOfMonth(viewDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < firstDay; i++) nodes.push(<div key={`e-${i}`} className="w-8 h-8" />);

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(viewDate.getFullYear(), viewDate.getMonth(), d);

      // Check if date is in the past (before today)
      const isPastDate = disablePastDates && date < today;
      const isOutOfRange = (!!minDay && date < minDay) || (!!maxDay && date > maxDay);
      const isDisabled = isPastDate || isOutOfRange;

      const isSelectedStart = isSameDay(date, tempStart);
      const isSelectedEnd = isSameDay(date, tempEnd);

      const isHovering = hoveredDate && tempStart && !tempEnd;

      let isInRange = false;
      let isRangeStart = false;
      let isRangeEnd = false;

      if (tempStart && tempEnd) {
        if (isSameDay(date, tempStart)) isRangeStart = true;
        if (isSameDay(date, tempEnd)) isRangeEnd = true;
        if (inRange(date, tempStart, tempEnd)) isInRange = true;
      } else if (isHovering) {
        if (hoveredDate > tempStart) {
          if (isSameDay(date, tempStart)) isRangeStart = true;
          if (isSameDay(date, hoveredDate)) isRangeEnd = true;
          if (inRange(date, tempStart, hoveredDate)) isInRange = true;
        } else {
          if (isSameDay(date, hoveredDate)) isRangeStart = true;
          if (isSameDay(date, tempStart)) isRangeEnd = true;
          if (inRange(date, hoveredDate, tempStart)) isInRange = true;
        }
      }

      nodes.push(
        <button
          key={d}
          onClick={() => !isDisabled && handleSelect(date)}
          disabled={isDisabled}
          onMouseEnter={() => !isDisabled && tempStart && !tempEnd && setHoveredDate(date)}
          onMouseLeave={() => tempStart && !tempEnd && setHoveredDate(null)}
          className={cn(
            "transition-all duration-200 focus:outline-none relative font-medium cursor-pointer",
            sizeStyles[size].dayCell,
            // Disabled/past date state
            isDisabled && "opacity-30 cursor-not-allowed text-muted-foreground",
            // Default state
            !isDisabled && !isInRange && !isRangeStart && !isRangeEnd && "hover:bg-accent/80 hover:text-accent-foreground hover:scale-105 rounded-lg",

            // Range selection styling - smooth continuous background with gradient
            isInRange && "bg-primary/15 text-foreground",
            (isRangeStart || isRangeEnd) &&
              "bg-linear-to-br from-primary to-primary/80 text-primary-foreground hover:from-primary hover:to-primary/70 shadow-lg shadow-primary/25",

            // Only round the actual start and end of the range
            isRangeStart && !isRangeEnd && "rounded-l-lg rounded-r-none",
            isRangeEnd && !isRangeStart && "rounded-r-lg rounded-l-none",
            isRangeStart && isRangeEnd && "rounded-lg", // Single day selection

            // Hover effects for range
            isInRange && !isDisabled && "hover:bg-primary/25",

            !isDisabled && "focus:bg-accent focus:text-accent-foreground focus:z-10 focus:shadow-md",
          )}
        >
          {d}
        </button>,
      );
    }
    return nodes;
  };

  const renderMonthSelector = () => {
    const months =
      locale === "vi"
        ? ["Th1", "Th2", "Th3", "Th4", "Th5", "Th6", "Th7", "Th8", "Th9", "Th10", "Th11", "Th12"]
        : ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

    return (
      <div className="grid grid-cols-3 gap-2 p-2">
        {months.map((month, idx) => {
          const isSelected = viewDate.getMonth() === idx;
          return (
            <button
              key={month}
              type="button"
              onClick={() => {
                setViewDate(new Date(viewDate.getFullYear(), idx, 1));
                setViewMode("calendar");
              }}
              className={cn(
                "py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200",
                isSelected ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent/80 text-foreground hover:scale-105",
              )}
            >
              {month}
            </button>
          );
        })}
      </div>
    );
  };

  const renderYearSelector = () => {
    const startYear = Math.floor(viewDate.getFullYear() / 12) * 12;
    const years = Array.from({ length: 12 }, (_, i) => startYear + i);

    return (
      <div className="grid grid-cols-3 gap-2 p-2">
        {years.map((year) => {
          const isSelected = viewDate.getFullYear() === year;
          return (
            <button
              key={year}
              type="button"
              onClick={() => {
                setViewDate(new Date(year, viewDate.getMonth(), 1));
                setViewMode("month");
              }}
              className={cn(
                "py-2 px-3 rounded-lg text-sm font-medium transition-all duration-200",
                isSelected ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-accent/80 text-foreground hover:scale-105",
              )}
            >
              {year}
            </button>
          );
        })}
      </div>
    );
  };

  const isTodayUnavailable = (!!minDay && todayDate < minDay) || (!!maxDay && todayDate > maxDay);

  const panel = (
    <div ref={wheelContainerRef} className="w-full">
      {/* Header */}
      <div className={cn("flex items-center justify-between px-1", sizeStyles[size].headerMargin)}>
        <button
          type="button"
          onClick={() => (viewMode === "year" ? navigateYearRange("prev") : navigateMonth("prev"))}
          className={cn(
            "rounded-xl transition-all duration-200",
            sizeStyles[size].navButton,
            "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground",
            "hover:scale-110 active:scale-95 hover:shadow-md",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
          )}
        >
          <ChevronLeft className={sizeStyles[size].navIcon} />
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setViewMode(viewMode === "month" ? "calendar" : "month")}
            className={cn(
              "rounded-lg px-2 py-0.5 font-bold transition-colors duration-200",
              size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm",
              viewMode === "month" ? "bg-primary/15 text-primary" : "text-foreground hover:bg-accent/50",
            )}
          >
            {viewDate.toLocaleDateString(locale === "vi" ? "vi-VN" : "en-US", { month: "long" })}
          </button>
          <button
            type="button"
            onClick={() => setViewMode(viewMode === "year" ? "calendar" : "year")}
            className={cn(
              "rounded-lg px-2 py-0.5 font-bold transition-colors duration-200",
              size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm",
              viewMode === "year" ? "bg-primary/15 text-primary" : "text-foreground hover:bg-accent/50",
            )}
          >
            {viewDate.getFullYear()}
          </button>
        </div>
        <button
          type="button"
          onClick={() => (viewMode === "year" ? navigateYearRange("next") : navigateMonth("next"))}
          className={cn(
            "rounded-xl transition-all duration-200",
            sizeStyles[size].navButton,
            "bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground",
            "hover:scale-110 active:scale-95 hover:shadow-md",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50",
          )}
        >
          <ChevronRight className={sizeStyles[size].navIcon} />
        </button>
      </div>
      {viewMode === "calendar" && (
        <>
          <div className={cn("grid grid-cols-7 gap-1 px-0.5", size === "sm" ? "mb-1" : size === "lg" ? "mb-3" : "mb-2")}>
            {(locale === "vi" ? ["CN", "T2", "T3", "T4", "T5", "T6", "T7"] : ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"]).map((d, idx) => (
              <div
                key={d}
                className={cn(
                  "text-center font-bold uppercase tracking-wide",
                  sizeStyles[size].weekdayLabel,
                  idx === 0 || idx === 6 ? "text-primary/70" : "text-muted-foreground/60",
                )}
              >
                {d}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-0.5 p-1 rounded-xl bg-muted/20">{renderGrid()}</div>
        </>
      )}
      {viewMode === "month" && renderMonthSelector()}
      {viewMode === "year" && renderYearSelector()}

      <div className={cn("flex items-center border-t border-border/50", sizeStyles[size].footerMargin)}>
        <button
          type="button"
          onClick={handleSelectToday}
          disabled={isTodayUnavailable}
          className={cn(
            "flex-1 font-semibold rounded-xl",
            "bg-linear-to-r from-primary/10 to-primary/5 border border-primary/30",
            "text-primary hover:from-primary/20 hover:to-primary/10 hover:border-primary/50",
            "transition-all duration-300 flex items-center justify-center",
            "hover:scale-[1.02] active:scale-[0.98] hover:shadow-md hover:shadow-primary/10",
            sizeStyles[size].actionButton,
            isTodayUnavailable && "opacity-50 cursor-not-allowed hover:scale-100 active:scale-100",
          )}
        >
          <Sparkles className={sizeStyles[size].actionIcon} />
          {t("today")}
        </button>
        <button
          type="button"
          onClick={handleClear}
          className={cn(
            "flex-1 font-semibold rounded-xl",
            "bg-linear-to-r from-destructive/10 to-destructive/5 border border-destructive/30",
            "text-destructive hover:from-destructive/20 hover:to-destructive/10 hover:border-destructive/50",
            "transition-all duration-300 flex items-center justify-center",
            "hover:scale-[1.02] active:scale-[0.98] hover:shadow-md hover:shadow-destructive/10",
            sizeStyles[size].actionButton,
          )}
        >
          <XIcon className={sizeStyles[size].actionIcon} />
          {t("clear")}
        </button>
      </div>
    </div>
  );

  const displayFormat = (date: Date) => formatDateShort(date);

  const displayLabel =
    tempStart && tempEnd ? `${displayFormat(tempStart)} - ${displayFormat(tempEnd)}` : tempStart ? `${displayFormat(tempStart)} - ...` : placeholder;
  const effectiveError = localRequiredError;
  const autoId = useId();
  const resolvedId = id ? String(id) : `daterangepicker-${autoId}`;
  const labelId = label ? `${resolvedId}-label` : undefined;

  return (
    <div className={cn("space-y-1.5", className)}>
      {label && (
        <label
          id={labelId}
          htmlFor={resolvedId}
          onClick={() => inputRef.current?.focus()}
          className={cn(
            size === "sm" ? "text-xs" : size === "lg" ? "text-base" : "text-sm",
            "font-medium transition-colors duration-300",
            disabled ? "text-muted-foreground cursor-not-allowed" : "text-foreground group-focus-within:text-primary hover:text-primary cursor-pointer",
            effectiveError && "text-destructive",
            labelClassName
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}
      <input
        tabIndex={-1}
        aria-hidden="true"
        value={startDate && endDate ? "selected" : ""}
        onChange={() => {}}
        required={required}
        disabled={disabled}
        onInvalid={(e) => {
          e.preventDefault();
          setLocalRequiredError(tv("required"));
        }}
        className="pointer-events-none absolute h-0 w-0 opacity-0"
      />
      <Popover
        open={isOpen}
        onOpenChange={setIsOpen}
        placement="bottom-start"
        disabled={disabled}
        contentWidth={sizeStyles[size].contentWidth}
        contentClassName={cn(
          "p-0",
          "backdrop-blur-xl bg-popover/95 border-border/40 shadow-2xl",
          "rounded-2xl md:rounded-3xl",
          "max-w-[calc(100vw-1rem)] max-h-[calc(100vh-6rem)] overflow-auto overscroll-contain",
          sizeStyles[size].contentPadding,
          "animate-in fade-in-0 zoom-in-95 slide-in-from-top-2 duration-300",
        )}
        trigger={
          <div
            ref={triggerRef}
            id={resolvedId}
            role="button"
            aria-label={tempStart ? displayLabel : placeholder}
            aria-labelledby={labelId}
            aria-required={required}
            aria-invalid={!!effectiveError}
            className={cn(
              "group flex w-full items-center justify-between rounded-full border bg-background/80 backdrop-blur-sm",
              sizeStyles[size].trigger,
              disabled
                ? "border-border/40 opacity-50 cursor-not-allowed"
                : [
                    "border-border/60 hover:border-primary/40",
                    "focus-within:ring-2 focus-within:ring-primary/50 focus-within:ring-offset-2 focus-within:ring-offset-background focus-within:border-transparent focus-within:hover:border-transparent",
                    "hover:bg-accent/10 hover:shadow-lg hover:shadow-primary/5 hover:-translate-y-0.5",
                  ],
              "transition-all duration-300 ease-out",
              isOpen && !isFocused && "ring-2 ring-primary/30 border-primary/50 shadow-lg shadow-primary/10",
              effectiveError && "border-destructive/60 focus-within:ring-destructive/50 bg-destructive/5",
            )}
          >
            <div className={cn("flex items-center flex-1 min-w-0", size === "sm" ? "gap-1.5" : "gap-2.5")}>
              <div
                className={cn(
                  "flex items-center justify-center transition-colors duration-300",
                  effectiveError ? "text-destructive" : isOpen ? "text-primary" : "text-muted-foreground group-hover:text-primary",
                )}
              >
                <Calendar className={cn("transition-transform duration-300", sizeStyles[size].calendarIcon, isOpen && "scale-110")} />
              </div>
              <input
                ref={inputRef}
                type="text"
                aria-label={tempStart ? displayLabel : placeholder}
                disabled={disabled}
                placeholder={placeholder}
                value={isFocused ? inputValue : (tempStart ? displayLabel : "")}
                onChange={handleInputChange}
                onFocus={() => {
                  setIsFocused(true);
                  if (!disabled) setIsOpen(true);
                }}
                onBlur={handleInputBlur}
                onKeyDown={handleInputKeyDown}
                onClick={(e) => {
                  if (isOpen) {
                    e.stopPropagation();
                  }
                }}
                className="w-full bg-transparent border-none outline-none focus:outline-none p-0 text-foreground font-medium placeholder-muted-foreground/60 min-w-0"
              />
            </div>
            <span className={cn("transition-all duration-300 text-muted-foreground group-hover:text-foreground", isOpen && "rotate-180 text-primary")}>
              <svg className={cn(sizeStyles[size].navIcon)} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </span>
          </div>
        }
      >
        {panel}
      </Popover>
      {effectiveError && <div className="text-xs text-destructive">{effectiveError}</div>}
    </div>
  );
};

export const CompactDatePicker: React.FC<{
  value?: Date;
  onChange: (date?: Date) => void;
  className?: string;
}> = ({ value, onChange, className }) => {
  return (
    <DatePicker
      value={value}
      onChange={onChange as (d: Date | undefined) => void}
      size="sm"
      className={cn("max-w-56", className)}
      placeholder="Date"
    />
  );
};
